// @ts-nocheck
import AlgoliaCustomHits from '@components/algolia/algolia-custom-hits'
import AlgoliaHit from '@components/algolia/AlgoliaHit'
import ClearAllFilters from '@components/algolia/ClearAllFilters/ClearAllFilters'
import NoOfFilters from '@components/algolia/NoOfFilters/NoOfFilters'
import AlgoliaFilters from '@components/collection/AlgoliaFilters'
import { Close, FilterAlt, Grid1, Grid2, Grid6 } from '@components/icons'
import {
  debounceTime,
  getDefaultSearchString,
  searchStateToURL,
} from '@lib/algolia'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import QueryString from 'qs'
import React, { useEffect, useRef, useState } from 'react'
import { InstantSearchProps } from 'react-instantsearch-core'
import {
  Configure,
  InstantSearch,
  SearchBox,
  Stats,
} from 'react-instantsearch-dom'

import cn from 'clsx'

const GRID_LAYOUT_6 = 'GRID_LAYOUT_6'
const GRID_LAYOUT_4 = 'GRID_LAYOUT_4'
const GRID_LAYOUT_1 = 'GRID_LAYOUT_1'

interface AlgoliaCollectionProps extends InstantSearchProps {
  defaultCategory: string
  searchTerm?: string
  pathname?: string
  exactIndexName?: string
}

const AlgoliaCollection = ({
  defaultCategory,
  searchClient,
  indexName,
  resultsState,
  searchState,
  searchTerm,
  pathname,
  exactIndexName,
  ...rest
}: AlgoliaCollectionProps) => {
  const [hitsPerPage] = useState(36)
  const [localSearchState, setLocalSearchState] = useState(searchState)
  const [debounceState, setDebounceState] = useState<NodeJS.Timeout | null>(
    null
  )
  const [filter, setFilter] = useState(false)
  const [specialPrice, setSpecialPrice] = useState(false)
  const [promoOff, setPromoOff] = useState(false)
  const [gridSelectionDesktop, setGridSelectionDesktop] =
    useState(GRID_LAYOUT_4)
  const [gridSelectionMobile, setGridSelectionMobile] = useState(GRID_LAYOUT_4)
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>
  const isInitialMount = useRef(true)

  useEffect(() => {
    setLocalSearchState(searchState)
  }, [searchState])

  useEffect(() => {
    // Skip scrolling on the initial mount to allow scroll restoration
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (!localSearchState?.page) {
      return
    }
    document.querySelector('[data-id="collection-wrapper"]')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [localSearchState?.page])

  // Keep refs in sync so the debounced URL writer reads the latest toggle
  // values without stale closures.
  const specialPriceRef = useRef(specialPrice)
  const promoOffRef = useRef(promoOff)
  useEffect(() => {
    specialPriceRef.current = specialPrice
  }, [specialPrice])
  useEffect(() => {
    promoOffRef.current = promoOff
  }, [promoOff])

  // Hydrate the Clearance / Special Offers toggles from the URL so a shared or
  // returned-to link restores the same filtered results.
  useEffect(() => {
    const params = QueryString.parse(
      window?.location?.search?.replace(/^\?/, '') ?? ''
    )
    const sp = params.clearance === '1'
    const po = params.offers === '1'
    specialPriceRef.current = sp
    promoOffRef.current = po
    setSpecialPrice(sp)
    setPromoOff(po)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset the toggles when navigating to a different collection so an active
  // Clearance / Special Offers filter doesn't silently carry over and quietly
  // limit what the next listing shows.
  const prevCategoryRef = useRef(defaultCategory)
  useEffect(() => {
    if (prevCategoryRef.current === defaultCategory) return
    prevCategoryRef.current = defaultCategory
    setSpecialPrice(false)
    setPromoOff(false)
    specialPriceRef.current = false
    promoOffRef.current = false
  }, [defaultCategory])

  // Build the URL from the search state plus the toggle params so both the
  // InstantSearch state and the filter toggles round-trip in the query string.
  const buildToggleUrl = (state: any, sp: boolean, po: boolean) => {
    let query = searchStateToURL(state)
    const extras: string[] = []
    if (sp) extras.push('clearance=1')
    if (po) extras.push('offers=1')
    if (extras.length) {
      query = query ? `${query}&${extras.join('&')}` : `?${extras.join('&')}`
    }
    return `${window.location.pathname}${query}`
  }

  const replaceUrl = (path: string) => {
    window.history.replaceState(
      { ...window.history.state, as: path, url: path },
      '',
      path
    )
  }

  const onSearchStateChange = (newSearchState: any) => {
    if (debounceState) {
      clearTimeout(debounceState)
    }
    const mergedState = { ...localSearchState, ...newSearchState }
    const newDebounceState = setTimeout(() => {
      replaceUrl(
        buildToggleUrl(
          mergedState,
          specialPriceRef.current,
          promoOffRef.current
        )
      )
    }, debounceTime)

    setLocalSearchState(mergedState)
    setDebounceState(newDebounceState)
  }

  const handleSpecialPriceChange = (value: boolean) => {
    // Clearance and Special Offers are mutually exclusive (their filters can
    // never both match), so enabling one turns the other off.
    const nextPromoOff = value ? false : promoOffRef.current
    setSpecialPrice(value)
    specialPriceRef.current = value
    setPromoOff(nextPromoOff)
    promoOffRef.current = nextPromoOff
    replaceUrl(buildToggleUrl(localSearchState, value, nextPromoOff))
  }

  const handlePromoOffChange = (value: boolean) => {
    const nextSpecialPrice = value ? false : specialPriceRef.current
    setPromoOff(value)
    promoOffRef.current = value
    setSpecialPrice(nextSpecialPrice)
    specialPriceRef.current = nextSpecialPrice
    replaceUrl(buildToggleUrl(localSearchState, nextSpecialPrice, value))
  }

  // Reset both Configure-based toggles when "Clear all filters" is used, since
  // ClearRefinements only knows about standard refinements.
  const handleClearToggles = () => {
    setSpecialPrice(false)
    setPromoOff(false)
    specialPriceRef.current = false
    promoOffRef.current = false
    replaceUrl(buildToggleUrl(localSearchState, false, false))
  }

  const toggleFilter = () => {
    filter ? closeFilter() : openFilter()
  }

  const openFilter = () => {
    try {
      disableBodyScroll(ref.current)
    } catch (e: any) {
      console.log(e.error)
    }
    setFilter(true)
  }

  const closeFilter = () => {
    clearAllBodyScrollLocks()
    setFilter(false)
  }

  if (!indexName) {
    return null
  }

  const totalProducts = (
    <Stats
      translations={{
        stats(nbHits: any) {
          return `${nbHits.toLocaleString()}`
        },
      }}
    />
  )

  const defaultFilterString = getDefaultSearchString(
    defaultCategory?.length ? `collections:${defaultCategory}` : undefined
  )

  const filterString = specialPrice
    ? `${defaultFilterString} AND compare_at_price > 0`
    : defaultFilterString

  const filterClauses = [filterString]
  // Hide sold-out products on category PLPs (collections/brands) so distinct
  // picks an in-stock variant. Skipped on search (no defaultCategory) so a
  // searched-for sold-out item can still surface for "Enquire now".
  if (defaultCategory) filterClauses.push('inventory_quantity > 0')
  if (promoOff) filterClauses.push('meta.custom.is_promo_off:true')
  const composedFilterString = filterClauses.filter(Boolean).join(' AND ')

  return (
    <div className="algolia_collection" data-id="collection-grid-wrapper">
      <InstantSearch
        searchClient={searchClient}
        indexName={exactIndexName ?? indexName}
        resultsState={resultsState}
        searchState={localSearchState}
        onSearchStateChange={onSearchStateChange}
        stalledSearchDelay={400}
        {...rest}
      >
        <Configure
          hitsPerPage={hitsPerPage}
          filters={composedFilterString}
          ruleContexts={[defaultCategory]}
          distinct
          facetingAfterDistinct
        />
        {searchTerm && (
          <div className="hidden">
            <SearchBox defaultRefinement={searchTerm} />
          </div>
        )}
        <div className="algolia_collection__product_list">
          <div className="flex flex-col">
            {/* filtering Panel starts */}
            <div className="sticky top-14 lg:top-19 z-20 bg-brand-white">
              <div className="py-4 flex items-center">
                <div className="flex flex-1">
                  <Grid2
                    className="hidden lg:block mr-4 cursor-pointer"
                    color={`${
                      gridSelectionDesktop === GRID_LAYOUT_4
                        ? 'inherit'
                        : '#CECECE'
                    }`}
                    onClick={() => setGridSelectionDesktop(GRID_LAYOUT_4)}
                  />
                  <Grid6
                    className="hidden lg:block  cursor-pointer"
                    color={`${
                      gridSelectionDesktop !== GRID_LAYOUT_6
                        ? '#CECECE'
                        : 'inherit'
                    }`}
                    onClick={() => setGridSelectionDesktop(GRID_LAYOUT_6)}
                  />
                  <Grid2
                    className="block lg:hidden"
                    color={`${
                      gridSelectionMobile !== GRID_LAYOUT_4
                        ? '#CECECE'
                        : 'inherit'
                    }`}
                    onClick={() => setGridSelectionMobile(GRID_LAYOUT_4)}
                  />
                  <Grid1
                    className="block lg:hidden ml-4"
                    color={`${
                      gridSelectionMobile === GRID_LAYOUT_1
                        ? 'inherit'
                        : '#CECECE'
                    }`}
                    onClick={() => setGridSelectionMobile(GRID_LAYOUT_1)}
                  />
                </div>
                <div className="body-2 text-text-disabled font-normal hidden lg:flex flex-1 justify-center">
                  {totalProducts}
                  <span className="pl-1">Products</span>
                </div>
                <div
                  className="flex items-center justify-end flex-1 cursor-pointer"
                  onClick={toggleFilter}
                >
                  <div className="ml-2">
                    <NoOfFilters
                      hideIfZero={true}
                      extraCount={(specialPrice ? 1 : 0) + (promoOff ? 1 : 0)}
                    />
                  </div>
                  <span className="label-bold text-text-subdued pr-2.5">
                    FILTER & SORT
                  </span>
                  <span>
                    <FilterAlt />
                  </span>
                </div>
              </div>

              {/* end of toolbar */}
              <div className="border-b lg:border-ui-light-grey"></div>
            </div>
            {/* filtering Panel ends */}
            <div className="body-2 text-text-disabled font-normal flex lg:hidden justify-center pt-2 lg:pt-0">
              {totalProducts}
              <span className="pl-1">Products</span>
            </div>
            {/* Filter & Sort sidebar */}
            <div
              role="dialog"
              aria-modal={filter}
              aria-label="Filter and sort"
              aria-hidden={!filter}
            >
              {/* backdrop */}
              <div
                className={cn(
                  'fixed inset-0 z-40 bg-black bg-opacity-40 transition-opacity duration-300',
                  {
                    'opacity-100': filter,
                    'opacity-0 pointer-events-none': !filter,
                  }
                )}
                onClick={closeFilter}
              />
              {/* drawer */}
              <div
                className={cn(
                  'fixed left-0 right-0 bottom-0 z-50 flex flex-col bg-brand-polar-white shadow-xl transform transition-transform duration-300 ease-in-out',
                  'max-h-[90vh] rounded-t-2xl',
                  'lg:top-0 lg:bottom-0 lg:left-auto lg:right-0 lg:h-full lg:max-h-full lg:w-full lg:max-w-md lg:rounded-none',
                  {
                    'translate-y-0 lg:translate-x-0': filter,
                    'translate-y-full lg:translate-y-0 lg:translate-x-full':
                      !filter,
                  }
                )}
              >
                {/* header */}
                <div className="px-6 pt-3 lg:pt-5">
                  {/* mobile drag handle */}
                  <div className="flex justify-center pb-2 lg:hidden">
                    <span className="h-1 w-10 rounded-full bg-ui-grey-25" />
                  </div>
                  {/* desktop close */}
                  <div className="hidden lg:flex justify-end">
                    <button
                      type="button"
                      className="cursor-pointer p-1"
                      onClick={closeFilter}
                      aria-label="Close filters"
                    >
                      <Close />
                    </button>
                  </div>
                  <div className="flex justify-center pb-4 pt-4 lg:pt-6">
                    <span className="body-1-bold uppercase tracking-normal text-text-base">
                      Filter &amp; Sort
                    </span>
                  </div>
                </div>
                {/* body */}
                <div ref={ref} className="flex-1 overflow-y-auto px-6 pb-8">
                  <AlgoliaFilters
                    indexName={indexName}
                    collectionHandle={defaultCategory}
                    defaultSortIndex={exactIndexName}
                    specialPrice={specialPrice}
                    onSpecialPriceChange={handleSpecialPriceChange}
                    promoOff={promoOff}
                    onPromoOffChange={handlePromoOffChange}
                  />
                </div>
                {/* footer */}
                <div className="border-t border-ui-grey-25 px-6 pt-4 pb-6 bg-brand-polar-white">
                  <button
                    type="button"
                    className="w-full flex justify-center bg-brand-chrome text-white label-bold py-4 cursor-pointer"
                    onClick={closeFilter}
                  >
                    <span className="pr-1">Show</span>
                    <span className="pr-1">{totalProducts}</span>
                    <span>results</span>
                  </button>
                  <div className="flex justify-center pt-4">
                    <ClearAllFilters
                      extraActive={specialPrice || promoOff}
                      onClearExtra={handleClearToggles}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* end Filter & Sort sidebar */}
            <div
              className={cn(
                'infiniteHits lg:transition-all lg:ease-in-out lg:duration-500',
                { expanded: filter }
              )}
            >
              <AlgoliaCustomHits
                HitComponent={AlgoliaHit}
                totalProducts={totalProducts}
                gridSelectionDesktop={gridSelectionDesktop}
                gridSelectionMobile={gridSelectionMobile}
                pathname={pathname}
                page={localSearchState.page}
              />
            </div>
          </div>
        </div>
      </InstantSearch>
      <style jsx global>
        {`
          .ais-ClearRefinements-button {
            font-family: 'Lato', Arial, sans-serif;
            font-weight: 400;
            font-size: 13px;
            line-height: 24px;
            text-decoration: underline;
            text-underline-position: under;
            cursor: pointer;
            color: rgb(71, 71, 71);
          }
        `}
      </style>
    </div>
  )
}

export default AlgoliaCollection
